/**
 * GST Utilities for Indian Tax Invoices & Compliance
 * Provides state code directory and automatic derivation from GSTIN.
 */

export const GST_STATE_MAP = Object.freeze({
  "01": "Jammu & Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  "10": "Bihar",
  "11": "Sikkim",
  "12": "Arunachal Pradesh",
  "13": "Nagaland",
  "14": "Manipur",
  "15": "Mizoram",
  "16": "Tripura",
  "17": "Meghalaya",
  "18": "Assam",
  "19": "West Bengal",
  "20": "Jharkhand",
  "21": "Odisha",
  "22": "Chhattisgarh",
  "23": "Madhya Pradesh",
  "24": "Gujarat",
  "25": "Daman & Diu",
  "26": "Dadra & Nagar Haveli and Daman & Diu",
  "27": "Maharashtra",
  "28": "Andhra Pradesh (Old)",
  "29": "Karnataka",
  "30": "Goa",
  "31": "Lakshadweep",
  "32": "Kerala",
  "33": "Tamil Nadu",
  "34": "Puducherry",
  "35": "Andaman & Nicobar Islands",
  "36": "Telangana",
  "37": "Andhra Pradesh",
  "38": "Ladakh",
  "97": "Other Territory",
});

/**
 * Returns state name and 2-digit state code from GSTIN.
 * In India, the first two characters of a 15-character GSTIN represent the state code.
 */
export function getStateInfoFromGstin(gstin) {
  if (!gstin || typeof gstin !== "string") {
    return { stateCode: "", stateName: "" };
  }
  const clean = gstin.trim().toUpperCase();
  if (clean.length < 2) {
    return { stateCode: "", stateName: "" };
  }
  const prefix = clean.slice(0, 2);
  const matchedName = GST_STATE_MAP[prefix];
  if (matchedName) {
    return { stateCode: prefix, stateName: matchedName };
  }
  // If numeric prefix exists but not in standard map
  if (/^\d{2}$/.test(prefix)) {
    return { stateCode: prefix, stateName: "" };
  }
  return { stateCode: "", stateName: "" };
}

/**
 * Lookup 2-digit GST state code from a state name string
 */
export function getStateCodeFromName(stateName) {
  if (!stateName || typeof stateName !== "string") return "";
  const normalized = stateName.trim().toLowerCase();
  for (const [code, name] of Object.entries(GST_STATE_MAP)) {
    if (name.toLowerCase() === normalized) {
      return code;
    }
  }
  return "";
}

export const GST_STATE_OPTIONS = Object.entries(GST_STATE_MAP).map(([code, name]) => ({
  value: name,
  code,
  label: `${code} - ${name}`,
}));

/**
 * Determine if a transaction is an inter-state supply (IGST applies)
 * Returns true if seller and buyer states are different, false for intra-state (CGST+SGST).
 */
export function isInterStateSupply(sellerCode, buyerCode, sellerName, buyerName) {
  const sCode = (sellerCode || getStateCodeFromName(sellerName) || "").trim();
  const bCode = (buyerCode || getStateCodeFromName(buyerName) || "").trim();

  if (sCode && bCode) {
    return sCode !== bCode;
  }

  const sName = (sellerName || "").trim().toLowerCase();
  const bName = (buyerName || "").trim().toLowerCase();
  if (sName && bName) {
    return sName !== bName;
  }

  return false;
}

